import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { CheckCircle2, ShieldAlert, Cpu, ArrowRight } from 'lucide-react';
import SpotlightCard from '../ui/SpotlightCard';
import SpotlightButton from '../ui/SpotlightButton';
import AnimatedBadge from '../ui/AnimatedBadge';

import { useContact } from '../../context/ContactContext';

export default function FiscalSection() {
  const { openModal } = useContact();
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], ["-15%", "15%"]);

  return (
    <section 
      ref={containerRef}
      id="fiscal" 
      className="w-full bg-background py-24 relative overflow-hidden"
    >
      {/* Parallax Background Image */}
      <motion.div 
        style={{ y }}
        className="absolute inset-0 w-full h-[130%] pointer-events-none opacity-20 grayscale"
      >
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1454165833767-027ffea9e77b?auto=format&fit=crop&q=80')] bg-cover bg-center"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background"></div>
      </motion.div>

      <div className="max-w-7xl mx-auto px-6 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-20 items-center">
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7 text-center lg:text-left order-2 lg:order-1"
          >
            <div className="mb-4 md:mb-6 flex justify-center lg:justify-start">
              <AnimatedBadge>
                Escrita Fiscal & Digital
              </AnimatedBadge>
            </div>
            <h2 className="text-3xl md:text-5xl font-black text-foreground tracking-tight leading-tight mb-6 md:mb-8">
              Escrita Fiscal <br className="hidden md:block" /> 
              <span className="text-orange-500">Inteligente.</span>
            </h2>
            <p className="text-sm md:text-xl text-slate-400 font-medium mb-8 md:mb-12 leading-relaxed max-w-xl mx-auto lg:mx-0">
              Nossa operação processa dados em escala com auditoria digital rigorosa. 
              Garantimos que sua empresa navegue com segurança em qualquer cenário tributário.
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10 md:mb-12">
              <SpotlightCard innerClassName="p-6 md:p-8" radius="2rem">
                <CheckCircle2 size={24} className="text-orange-500 mb-4 mx-auto md:mx-0" />
                <h4 className="text-foreground font-bold text-lg mb-2">Conformidade Total</h4>
                <p className="text-slate-500 text-xs md:text-sm">Monitoramento 24/7 de todas as obrigações fiscais da sua empresa.</p>
              </SpotlightCard>
              <SpotlightCard innerClassName="p-6 md:p-8" radius="2rem">
                <ShieldAlert size={24} className="text-orange-500 mb-4 mx-auto md:mx-0" />
                <h4 className="text-foreground font-bold text-lg mb-2">Segurança SPED</h4>
                <p className="text-slate-500 text-xs md:text-sm">Pré-auditoria digital em todos os arquivos antes da transmissão oficial.</p>
              </SpotlightCard>
            </div>

            <div className="flex justify-center lg:justify-start px-6 md:px-0">
              <SpotlightButton href="https://wa.me/5511949830659" className="w-full md:w-auto">
                <div className="flex items-center gap-2">
                  Agendar Consultoria Fiscal
                  <ArrowRight size={16} className="text-orange-500" />
                </div>
              </SpotlightButton>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-5 relative order-1 lg:order-2"
          >
            <div className="relative z-10 rounded-[3rem] md:rounded-[4rem] overflow-hidden border border-foreground/10 aspect-[4/5] shadow-2xl max-w-md mx-auto">
              <img 
                src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&q=80" 
                alt="Equipe Fiscal JWCosta" 
                className="w-full h-full object-cover grayscale-[40%] hover:grayscale-0 transition-all duration-1000"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent opacity-60"></div>
            </div>
            
            <motion.div 
              animate={{ 
                y: [0, -15, 0],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="absolute -bottom-6 -right-2 md:-bottom-8 md:-left-8 bg-orange-600 p-6 md:p-8 rounded-[2rem] shadow-2xl z-20 max-w-[180px] md:max-w-[220px]"
            >
              <Cpu className="w-6 h-6 md:w-8 md:h-8 text-white mb-4" />
              <p className="text-white font-black text-2xl md:text-3xl mb-1">99.9%</p>
              <p className="text-white/80 text-[8px] md:text-[10px] font-black uppercase tracking-widest leading-tight">Auditoria sem Falhas</p>
            </motion.div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}
